import { postJson } from "./http";

/**
 * The onboarding client's lifecycle — drafts, the board, step progress,
 * answers and Zoho package sizing. Drafts live in onboarding_* tables and
 * stay out of `companies` until go-live.
 *
 * This file used to be the whole feature's API; the screen-specific surfaces
 * now live beside it (zohoConnectApi, zohoRecordsApi, banksApi, taxesApi,
 * currenciesApi, teamApi, seatsApi, escalationsApi, …) on the shared
 * plumbing in http.ts. What remains here is what store.ts drives.
 */

// Server shape (snake_case). Mapped to the domain CoClient in store.ts.
export interface OnboardingClientDTO {
  public_id: string;
  name: string | null;
  entity_type: string | null;
  subdomain: string | null;
  /** The tenant domain this environment serves — never assume initium. */
  tenant_domain?: string | null;
  business_nature_category: string | null;
  business_nature_description: string | null;
  backend_system: string | null;
  backend_status: string | null;
  intake_mode: string | null;
  bookkeeping_type: string | null;
  cfo_source: string | null;
  account_start_date: string | null;
  tally_company_name: string | null;
  tally_gstin: string | null;
  tally_gst_state: string | null;
  tally_gst_registration_type: string | null;
  status: string;
  current_step: string;
  company_id: number | null;
  live_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  services: string[];
  done_slugs: string[];
  step_progress: {
    step_slug: string;
    status: string;
    started_at: string | null;
    completed_at: string | null;
  }[];
  answers: Record<string, unknown>;
  zoho_sizing: {
    docs_per_year: string | null;
    user_count: number;
    multi_currency: boolean | null;
    gstin_count: number;
    recommended_plan: string | null;
    chosen_plan: string | null;
    driving_rules: string[];
    needs_zoho_call: boolean;
  } | null;
}

export interface OnboardingBoardRowDTO {
  public_id: string;
  name: string | null;
  subdomain: string | null;
  /** The tenant domain this environment serves — never assume initium. */
  tenant_domain?: string | null;
  status: string;
  current_step: string;
  services: string[];
  steps_done: number;
  created_at: string | null;
  updated_at: string | null;
}

export interface ZohoSizingRuleDTO {
  bucket_code: string | null;
  label: string | null;
  threshold_max: number | null;
  midpoint: number | null;
  min_plan_id: string | null;
  flag: string | null;
}

export interface ZohoPricingConfigDTO {
  plans: {
    id: string;
    label: string;
    price_per_month: number;
    users_included: number;
    plan_rank: number;
  }[];
  rules: Record<string, ZohoSizingRuleDTO[]>;
  settings: {
    calls_per_bill: number;
    working_days_divisor: number;
    extra_seat_price: number;
    api_ceiling_per_day: number;
    reserved_seats: number;
    pricing_source: string;
  };
}

interface ClientEnvelope {
  success: boolean;
  message: string;
  data: { client: OnboardingClientDTO };
}

interface BoardEnvelope {
  success: boolean;
  message: string;
  data: { clients: OnboardingBoardRowDTO[] };
}

interface PricingEnvelope {
  success: boolean;
  message: string;
  data: ZohoPricingConfigDTO;
}

export const createDraft = async (): Promise<OnboardingClientDTO> =>
  (await postJson<ClientEnvelope>("/api/onboarding/clients/create", {})).data.client;

export const listDrafts = async (includeAbandoned = false): Promise<OnboardingBoardRowDTO[]> =>
  (await postJson<BoardEnvelope>("/api/onboarding/clients/list", { include_abandoned: includeAbandoned })).data.clients;

export const fetchDraft = async (publicId: string): Promise<OnboardingClientDTO> =>
  (await postJson<ClientEnvelope>("/api/onboarding/clients/get", { public_id: publicId })).data.client;

export const patchDraft = async (
  publicId: string,
  patch: Record<string, unknown>
): Promise<OnboardingClientDTO> =>
  (await postJson<ClientEnvelope>("/api/onboarding/clients/update", { public_id: publicId, patch })).data.client;

export const putServices = async (publicId: string, services: string[]): Promise<OnboardingClientDTO> =>
  (await postJson<ClientEnvelope>("/api/onboarding/clients/services", { public_id: publicId, services })).data.client;

export interface SubdomainCheckDTO {
  /** The candidate after server-side cleanup — or the slug it derived from `name`. */
  subdomain: string;
  available: boolean;
  /** A free subdomain — the candidate itself when available, else a variant with a numeric tail. */
  suggestion: string;
  /** Is this a legal new address at all (grammar, length, not a pasted URL)? */
  valid: boolean;
  /** Why not, in words for the CA. Null when valid. */
  reason: string | null;
}

/**
 * Ask the server about an address. Pass `name` instead of `subdomain` to have
 * it derive the slug: the slug rules live on the server only, so the address
 * shown on screen is always the one that would be saved.
 */
export const checkSubdomain = async (
  publicId: string,
  subdomain: string,
  name?: string
): Promise<SubdomainCheckDTO> =>
  (
    await postJson<{ success: boolean; message: string; data: SubdomainCheckDTO }>(
      "/api/onboarding/clients/subdomain-check",
      { public_id: publicId, subdomain, ...(name === undefined ? {} : { name }) }
    )
  ).data;

export const saveProgress = async (
  publicId: string,
  stepSlug: string,
  status: "in_progress" | "done",
  nextSlug?: string
): Promise<OnboardingClientDTO> =>
  (
    await postJson<ClientEnvelope>("/api/onboarding/progress/save", {
      public_id: publicId,
      step_slug: stepSlug,
      status,
      ...(nextSlug ? { next_slug: nextSlug } : {}),
    })
  ).data.client;

export const saveAnswer = async (
  publicId: string,
  key: string,
  value: unknown
): Promise<OnboardingClientDTO> =>
  (await postJson<ClientEnvelope>("/api/onboarding/answers/save", { public_id: publicId, key, value })).data.client;

export const saveSizing = async (
  publicId: string,
  sizing: Record<string, unknown>
): Promise<OnboardingClientDTO> =>
  (await postJson<ClientEnvelope>("/api/onboarding/sizing/save", { public_id: publicId, ...sizing })).data.client;

export const abandonDraft = async (publicId: string): Promise<OnboardingClientDTO> =>
  (await postJson<ClientEnvelope>("/api/onboarding/clients/abandon", { public_id: publicId })).data.client;

export const fetchZohoPricing = async (): Promise<ZohoPricingConfigDTO> =>
  (await postJson<PricingEnvelope>("/api/onboarding/zoho-pricing", {})).data;
